import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Alert, Badge, Button, Card, Checkbox, Field, Select, Spinner, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { formatDay, isOpen, taskTone, todayIso, usePeople, useProject, useProjectForm } from '../projects/data';
import { type Priority, PRIORITIES, priorityName } from '../projects/requests-data';
import { buildRule, describeRule, formatMinutes, parseRule, type RecurrenceForm, type TaskDetail, useTask } from './data';
import { RecurrenceEditor } from './RecurrenceEditor';
import { useTaskStatus } from './useTaskStatus';

/** Detalle de una tarea (guía 7.3): de un proyecto o personal. Es la página a la que llevan los avisos. */
export function TaskDetailPage() {
  const { id = '' } = useParams();
  const task = useTask(id);
  if (!task.data) return task.error ? <Alert>{errorMessage(task.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const tk = task.data;
  const recurrence = describeRule(tk.recurrenceRule);

  return (
    <div className="space-y-4">
      <div>
        {tk.project ? (
          <Link to={`/proyectos/${tk.project.number}`} className="text-sm text-marca hover:underline">
            ← {tk.project.code} · {tk.project.name}
          </Link>
        ) : (
          <Link to="/tareas" className="text-sm text-marca hover:underline">
            ← {t('ta.titulo')}
          </Link>
        )}
        <h1 className="mt-1 text-2xl font-semibold text-tinta">{tk.title}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <Badge tone={taskTone(tk.statusCategory)}>{tk.status}</Badge>
          {tk.overdue && <Badge tone="red">{t('ta.atrasada')}</Badge>}
          {!tk.project && <span className="text-slate-500">· {t('ta.personal')}</span>}
          {recurrence && <span className="text-slate-500">· {t('ta.se_repite', { regla: recurrence })}</span>}
          {tk.recurrenceOfId && (
            <Link to={`/tareas/${tk.recurrenceOfId}`} className="text-marca hover:underline">
              · {t('ta.de_una_serie')}
            </Link>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-4">
          {tk.description && (
            <Card title={t('ta.descripcion')}>
              <p className="whitespace-pre-wrap text-sm text-slate-800">{tk.description}</p>
            </Card>
          )}
          <Subtasks task={tk} />
          <Comments task={tk} />
          <TimeCard task={tk} />
        </div>
        <div className="space-y-4">
          {tk.can.work && <StatusCard task={tk} />}
          <DetailsCard task={tk} />
          <Dependencies task={tk} />
          <Card variant="aside" title={t('ta.historial')}>
            <ol className="space-y-1 text-sm">
              {tk.history.map((h, i) => (
                <li key={`${h.changedAt}-${i}`} className="flex justify-between gap-2">
                  <span>{h.status}</span>
                  <span className="text-right text-xs text-slate-500">
                    {h.changedByName ?? ''} · {formatDay(h.changedAt)}
                  </span>
                </li>
              ))}
            </ol>
          </Card>
          {tk.can.edit && <DeleteCard task={tk} />}
        </div>
      </div>
    </div>
  );
}

function StatusCard({ task }: { task: TaskDetail }) {
  const form = useProjectForm();
  const status = useTaskStatus();
  const [statusId, setStatusId] = useState('');
  return (
    <Card title={t('ta.estado')}>
      <div className="space-y-2">
        {status.error && <Alert>{status.error}</Alert>}
        {status.blocked && (
          <Alert tone="warning">
            <p>{t('ta.bloqueada')}</p>
            <ul className="mt-1 list-disc pl-5">
              {status.blocked.blockers.map((b) => (
                <li key={b.id}>
                  <Link to={`/tareas/${b.id}`} className="underline">
                    {b.title}
                  </Link>{' '}
                  ({b.status})
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {status.next && (
          <Alert tone="success">
            {t('ta.siguiente_creada', { fecha: formatDay(status.next.dueDate) })}{' '}
            <Link to={`/tareas/${status.next.id}`} className="font-medium underline">
              {t('ta.ver_siguiente')}
            </Link>
          </Alert>
        )}
        <Select id="ta-estado" label={t('ta.nuevo_estado')} value={statusId} onChange={(e) => setStatusId(e.target.value)}>
          <option value="">{t('ta.elegir')}</option>
          {form.data?.taskStatuses
            .filter((s) => s.id !== task.statusId)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </Select>
        <Button
          disabled={status.busy || !statusId}
          onClick={() =>
            void status.change(task.id, statusId).then((ok) => {
              if (ok) setStatusId('');
            })
          }
        >
          {t('ta.cambiar_estado')}
        </Button>
      </div>
    </Card>
  );
}

function DetailsCard({ task }: { task: TaskDetail }) {
  const [editing, setEditing] = useState(false);
  if (editing) return <EditCard task={task} onClose={() => setEditing(false)} />;
  return (
    <Card variant="aside" title={t('ta.datos')}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-slate-500">{t('ta.asignados')}</dt>
        <dd>{task.assignees.length ? task.assignees.map((a) => a.fullName).join(', ') : <span className="text-slate-400">{t('ta.sin_asignar')}</span>}</dd>
        <dt className="text-slate-500">{t('ta.prioridad')}</dt>
        <dd>{priorityName(task.priority)}</dd>
        <dt className="text-slate-500">{t('ta.inicio')}</dt>
        <dd>{task.startDate ? formatDay(task.startDate) : '—'}</dd>
        <dt className="text-slate-500">{t('ta.vence')}</dt>
        <dd className={task.overdue ? 'font-medium text-red-700' : ''}>{task.dueDate ? formatDay(task.dueDate) : '—'}</dd>
        <dt className="text-slate-500">{t('ta.estimacion')}</dt>
        <dd>{task.estimateHours ? t('ta.horas', { n: task.estimateHours }) : '—'}</dd>
        <dt className="text-slate-500">{t('ta.creada_por')}</dt>
        <dd>{task.createdByName ?? '—'}</dd>
        {task.labels.length > 0 && (
          <>
            <dt className="text-slate-500">{t('ta.etiquetas')}</dt>
            <dd className="flex flex-wrap gap-1">
              {task.labels.map((l) => (
                <span key={l.id} className="rounded px-1.5 py-0.5 text-xs font-medium text-white" style={{ backgroundColor: l.color }}>
                  {l.name}
                </span>
              ))}
            </dd>
          </>
        )}
      </dl>
      {task.can.edit && (
        <Button variant="secondary" className="mt-3" onClick={() => setEditing(true)}>
          {t('ta.editar')}
        </Button>
      )}
    </Card>
  );
}

function EditCard({ task, onClose }: { task: TaskDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const form = useProjectForm();
  const people = usePeople(task.organizationId);
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [priority, setPriority] = useState<Priority>(task.priority);
  const [startDate, setStartDate] = useState(task.startDate ?? '');
  const [dueDate, setDueDate] = useState(task.dueDate ?? '');
  const [estimate, setEstimate] = useState(task.estimateHours ? String(task.estimateHours) : '');
  const [assignees, setAssignees] = useState(task.assignees.map((a) => a.id));
  const [labels, setLabels] = useState(task.labels.map((l) => l.id));
  const [recurrence, setRecurrence] = useState<RecurrenceForm>(parseRule(task.recurrenceRule));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Los asignados actuales siguen en la lista aunque ya no sean asignables.
  const candidates = [...(people.data ?? []), ...task.assignees.filter((a) => !people.data?.some((p) => p.id === a.id)).map((a) => ({ id: a.id, fullName: a.fullName ?? a.id, email: '' }))];

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/api/tareas/${task.id}`, {
        method: 'PATCH',
        json: {
          title: title.trim(),
          description: description.trim() || null,
          priority,
          startDate: startDate || null,
          dueDate: dueDate || null,
          estimateHours: estimate ? Number(estimate) : null,
          assigneeIds: assignees,
          labelIds: labels,
          // La regla es de la tarea que define la serie: una instancia no la cambia.
          ...(task.recurrenceOfId ? {} : { recurrenceRule: buildRule(recurrence) }),
        },
      });
      await Promise.all([qc.invalidateQueries({ queryKey: ['tareas'] }), qc.invalidateQueries({ queryKey: ['proyectos'] })]);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Card title={t('ta.editar')}>
      <form onSubmit={save} className="space-y-3">
        {error && <Alert>{error}</Alert>}
        <Field id="tae-titulo" label={t('ta.titulo_campo')} maxLength={300} required value={title} onChange={(e) => setTitle(e.target.value)} />
        <Textarea id="tae-descripcion" label={t('ta.descripcion')} rows={3} maxLength={10_000} value={description} onChange={(e) => setDescription(e.target.value)} />
        <Select id="tae-prioridad" label={t('ta.prioridad')} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {priorityName(p)}
            </option>
          ))}
        </Select>
        <div className="grid grid-cols-2 gap-3">
          <Field id="tae-inicio" type="date" label={t('ta.inicio')} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <Field id="tae-vence" type="date" label={t('ta.vence')} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
        <Field id="tae-estimacion" type="number" step="0.25" min="0.25" label={t('ta.estimacion_horas')} value={estimate} onChange={(e) => setEstimate(e.target.value)} />
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium text-slate-700">{t('ta.asignados')}</legend>
          <div className="max-h-40 space-y-1 overflow-y-auto">
            {candidates.map((p) => (
              <Checkbox key={p.id} label={p.fullName} checked={assignees.includes(p.id)} onChange={(e) => setAssignees(e.target.checked ? [...assignees, p.id] : assignees.filter((x) => x !== p.id))} />
            ))}
          </div>
        </fieldset>
        {!!form.data?.taskLabels.length && (
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium text-slate-700">{t('ta.etiquetas')}</legend>
            {form.data.taskLabels.map((l) => (
              <Checkbox key={l.id} label={l.name} checked={labels.includes(l.id)} onChange={(e) => setLabels(e.target.checked ? [...labels, l.id] : labels.filter((x) => x !== l.id))} />
            ))}
          </fieldset>
        )}
        {!task.recurrenceOfId && <RecurrenceEditor value={recurrence} onChange={setRecurrence} idPrefix="tae" />}
        <div className="flex gap-2">
          <Button type="submit" disabled={busy || !title.trim()}>
            {t('comun.guardar')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('comun.cancelar')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Tareas de las que depende (bloqueo duro a Terminado) y las que dependen de esta. */
function Dependencies({ task }: { task: TaskDetail }) {
  const qc = useQueryClient();
  const project = useProject(task.project ? String(task.project.number) : '', !!task.project && task.can.edit);
  const mine = useQuery({ queryKey: ['tareas', 'lista', 'mias'], queryFn: () => api<Array<{ id: string; title: string; projectId: string | null }>>('/api/tareas?alcance=mias'), enabled: !task.project && task.can.edit });
  const [blockedById, setBlockedById] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taken = new Set([task.id, ...task.blockedBy.map((b) => b.id)]);
  const options = task.project
    ? (project.data?.tasks ?? []).filter((x) => !taken.has(x.id)).map((x) => ({ id: x.id, title: x.title }))
    : (mine.data ?? []).filter((x) => !x.projectId && !taken.has(x.id));

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setBlockedById('');
      await Promise.all([qc.invalidateQueries({ queryKey: ['tareas'] }), qc.invalidateQueries({ queryKey: ['proyectos'] })]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!task.can.edit && task.blockedBy.length === 0 && task.blocks.length === 0) return null;
  return (
    <Card title={t('ta.dependencias')}>
      <div className="space-y-3 text-sm">
        {error && <Alert>{error}</Alert>}
        <div>
          <p className="text-xs font-semibold text-slate-500">{t('ta.depende_de')}</p>
          {task.blockedBy.length === 0 ? (
            <p className="text-slate-500">{t('ta.sin_dependencias')}</p>
          ) : (
            <ul className="space-y-1" aria-label={t('ta.depende_de')}>
              {task.blockedBy.map((b) => (
                <li key={b.id} className="flex items-center justify-between gap-2">
                  <Link to={`/tareas/${b.id}`} className={`hover:underline ${isOpen(b.statusCategory) ? '' : 'text-slate-500 line-through'}`}>
                    {b.title}
                  </Link>
                  <span className="flex items-center gap-2">
                    <Badge tone={taskTone(b.statusCategory)}>{b.status}</Badge>
                    {task.can.edit && (
                      <button
                        type="button"
                        className="px-1 text-slate-400 hover:text-red-700"
                        aria-label={t('ta.quitar_dependencia', { tarea: b.title })}
                        disabled={busy}
                        onClick={() => void act(() => api(`/api/tareas/${task.id}/dependencias/${b.id}`, { method: 'DELETE' }))}
                      >
                        ×
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {task.blocks.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500">{t('ta.bloquea_a')}</p>
            <ul className="space-y-1">
              {task.blocks.map((b) => (
                <li key={b.id}>
                  <Link to={`/tareas/${b.id}`} className="hover:underline">
                    {b.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        {task.can.edit && options.length > 0 && (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select id="ta-dependencia" label={t('ta.agregar_dependencia')} value={blockedById} onChange={(e) => setBlockedById(e.target.value)}>
                <option value="">{t('ta.elegir')}</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.title}
                  </option>
                ))}
              </Select>
            </div>
            <Button variant="secondary" disabled={busy || !blockedById} onClick={() => void act(() => api(`/api/tareas/${task.id}/dependencias`, { json: { blockedById } }))}>
              {t('ta.agregar')}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function Subtasks({ task }: { task: TaskDetail }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (task.subtasks.length === 0 && !task.can.edit) return null;

  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/tareas', { json: { projectId: task.projectId, parentId: task.id, title: title.trim(), organizationId: task.projectId ? undefined : task.organizationId } });
      setTitle('');
      await Promise.all([qc.invalidateQueries({ queryKey: ['tareas'] }), qc.invalidateQueries({ queryKey: ['proyectos'] })]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={task.project && !task.parentId ? t('ta.pasos') : t('ta.subtareas')}>
      <div className="space-y-3">
        {error && <Alert>{error}</Alert>}
        {task.subtasks.length === 0 ? (
          <p className="text-sm text-slate-500">{t('ta.sin_subtareas')}</p>
        ) : (
          <ul className="divide-y divide-slate-100" aria-label={t('ta.subtareas')}>
            {task.subtasks.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                <Link to={`/tareas/${s.id}`} className={`hover:underline ${isOpen(s.statusCategory) ? 'text-slate-800' : 'text-slate-500 line-through'}`}>
                  {s.title}
                </Link>
                <span className="flex items-center gap-2 text-xs text-slate-500">
                  {s.dueDate && <span>{formatDay(s.dueDate)}</span>}
                  <Badge tone={taskTone(s.statusCategory)}>{s.status}</Badge>
                </span>
              </li>
            ))}
          </ul>
        )}
        {task.can.edit && (
          <form onSubmit={add} className="flex items-end gap-2">
            <div className="flex-1">
              <Field id="ta-subtarea" label={t('ta.nueva_subtarea')} maxLength={300} value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <Button type="submit" variant="secondary" disabled={busy || !title.trim()}>
              {t('ta.agregar')}
            </Button>
          </form>
        )}
      </div>
    </Card>
  );
}

function Comments({ task }: { task: TaskDetail }) {
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/api/tareas/${task.id}/comentarios`, { json: { body: body.trim() } });
      setBody('');
      await qc.invalidateQueries({ queryKey: ['tareas', 'detalle', task.id] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('ta.comentarios')}>
      <div className="space-y-3">
        {task.comments.length === 0 ? (
          <p className="text-sm text-slate-500">{t('ta.sin_comentarios')}</p>
        ) : (
          <ol className="space-y-3" aria-label={t('ta.comentarios')}>
            {task.comments.map((c) => (
              <li key={c.id} className="rounded-md border border-slate-200 p-3">
                <p className="mb-1 text-xs text-slate-500">
                  <span className="font-medium text-slate-800">{c.author ?? '—'}</span> · {formatDay(c.createdAt)}
                </p>
                <p className="whitespace-pre-wrap text-sm text-slate-800">{c.body}</p>
              </li>
            ))}
          </ol>
        )}
        <form onSubmit={send} className="space-y-2 border-t border-slate-100 pt-3">
          {error && <Alert>{error}</Alert>}
          <Textarea id="ta-comentario" label={t('ta.escribir_comentario')} rows={3} maxLength={10_000} value={body} onChange={(e) => setBody(e.target.value)} />
          <Button type="submit" disabled={busy || !body.trim()}>
            {t('ta.comentar')}
          </Button>
        </form>
      </div>
    </Card>
  );
}

function TimeCard({ task }: { task: TaskDetail }) {
  const me = useMe();
  const qc = useQueryClient();
  const [spentOn, setSpentOn] = useState(todayIso());
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!task.can.work && task.time.length === 0) return null;

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['tareas', 'detalle', task.id] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  const log = (e: FormEvent) => {
    e.preventDefault();
    void act(async () => {
      await api(`/api/tareas/${task.id}/tiempo`, { json: { spentOn, minutes: Math.round(Number(hours) * 60), note: note.trim() || null } });
      setHours('');
      setNote('');
    });
  };

  return (
    <Card title={t('ta.tiempo_total', { total: formatMinutes(task.totalMinutes) })}>
      <div className="space-y-3">
        {error && <Alert>{error}</Alert>}
        {task.time.length > 0 && (
          <ul className="divide-y divide-slate-100 text-sm" aria-label={t('ta.tiempo')}>
            {task.time.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-2 py-1.5">
                <span>
                  <span className="font-medium">{formatMinutes(e.minutes)}</span> · {e.user ?? '—'} · {formatDay(e.spentOn)}
                  {e.note ? <span className="text-slate-500"> · {e.note}</span> : null}
                </span>
                {e.userId === me.id && (
                  <button
                    type="button"
                    className="px-1 text-slate-400 hover:text-red-700"
                    aria-label={t('ta.borrar_tiempo', { tiempo: formatMinutes(e.minutes) })}
                    disabled={busy}
                    onClick={() => void act(() => api(`/api/tareas/tiempo/${e.id}`, { method: 'DELETE' }))}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {task.can.work && (
          <form onSubmit={log} className="grid gap-2 sm:grid-cols-[9rem_7rem_1fr_auto] sm:items-end">
            <Field id="ta-tiempo-dia" type="date" label={t('ta.dia')} max={todayIso()} value={spentOn} onChange={(e) => setSpentOn(e.target.value)} />
            <Field id="ta-tiempo-horas" type="number" step="0.25" min="0.25" max="24" label={t('ta.horas_campo')} value={hours} onChange={(e) => setHours(e.target.value)} />
            <Field id="ta-tiempo-nota" label={t('ta.nota')} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
            <Button type="submit" variant="secondary" disabled={busy || !hours || Number(hours) <= 0}>
              {t('ta.registrar')}
            </Button>
          </form>
        )}
      </div>
    </Card>
  );
}

function DeleteCard({ task }: { task: TaskDetail }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/tareas/${task.id}`, { method: 'DELETE' });
      await Promise.all([qc.invalidateQueries({ queryKey: ['tareas'] }), qc.invalidateQueries({ queryKey: ['proyectos'] })]);
      void navigate(task.project ? `/proyectos/${task.project.number}` : '/tareas', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }
  return (
    <Card>
      <div className="space-y-2 text-sm">
        {error && <Alert>{error}</Alert>}
        {confirming ? (
          <Alert tone="warning">
            <p>{task.subtasks.length ? t('ta.borrar_confirmar_con_pasos', { n: task.subtasks.length }) : t('ta.borrar_confirmar')}</p>
            <div className="mt-2 flex gap-2">
              <Button variant="danger" disabled={busy} onClick={() => void remove()}>
                {t('ta.si_borrar')}
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                {t('comun.volver')}
              </Button>
            </div>
          </Alert>
        ) : (
          <Button variant="secondary" onClick={() => setConfirming(true)}>
            {t('ta.borrar')}
          </Button>
        )}
      </div>
    </Card>
  );
}
